import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { request } from "utils/request";

const API = () => process.env.REACT_APP_API_URL;

// Sentinel id for the read-only, computed system template. Backend exposes it
// at `/contracts/templates/system/` — no DB id, no timestamps. We give it a
// stable client-side id so routes (`/contracts/system`) and list lookups work
// uniformly with user rows.
export const SYSTEM_TEMPLATE_ID = "system";

// Normalize API shape → internal Redux shape. Backend field is `content`;
// the editor code was originally written against `content_html`, so we keep
// that alias in state and translate at the boundary.
const normalizeUserTemplate = (row) => ({
  id: row.id,
  name: row.name,
  is_default: !!row.is_default,
  is_system: false,
  content_html: row.content || "",
  updated_at: row.updated_at || null,
});

const normalizeSystemTemplate = (row) => ({
  id: SYSTEM_TEMPLATE_ID,
  name: row.name,
  is_default: !!row.is_default,
  is_system: true,
  content_html: row.content || "",
  updated_at: null,
});

const errorMessage = (error, fallback) =>
  error?.error || error?.detail || error?.message || fallback;

// --- Thunks -----------------------------------------------------------------

// Fetch the user's templates AND the system template in parallel, then merge
// into a single list. System entry is prepended so it consistently shows at
// the top of the list.
export const fetchContracts = createAsyncThunk(
  "contractManagement/fetchContracts",
  async (_arg, { rejectWithValue }) => {
    try {
      const [userResp, systemResp] = await Promise.all([
        request(`${API()}/contracts/templates/`, { method: "GET" }),
        request(`${API()}/contracts/templates/system/`, { method: "GET" }),
      ]);
      const userRows = Array.isArray(userResp)
        ? userResp
        : userResp?.results || [];
      const merged = [
        normalizeSystemTemplate(systemResp),
        ...userRows.map(normalizeUserTemplate),
      ];
      return { templates: merged, count: userResp?.count ?? userRows.length };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load templates."));
    }
  }
);

// Detail fetch. System sentinel routes to the system endpoint; everything
// else hits the standard retrieve view.
export const fetchContractById = createAsyncThunk(
  "contractManagement/fetchContractById",
  async (contractId, { rejectWithValue }) => {
    try {
      if (String(contractId) === SYSTEM_TEMPLATE_ID) {
        const data = await request(`${API()}/contracts/templates/system/`, {
          method: "GET",
        });
        return normalizeSystemTemplate(data);
      }
      const data = await request(
        `${API()}/contracts/templates/${contractId}/`,
        { method: "GET" }
      );
      return normalizeUserTemplate(data);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to load template."));
    }
  }
);

// Re-fetch the system row after any operation that could flip its computed
// `is_default`. Used as a follow-up from setContractAsDefault and
// deleteContract handlers. `fromDelete` distinguishes the two so we only
// stamp `deletedFallbackName` (used by the "system is now your default"
// toast copy) on the delete-of-default path.
export const refreshSystemTemplate = createAsyncThunk(
  "contractManagement/refreshSystemTemplate",
  async ({ fromDelete = false } = {}, { rejectWithValue }) => {
    try {
      const data = await request(`${API()}/contracts/templates/system/`, {
        method: "GET",
      });
      return { row: normalizeSystemTemplate(data), fromDelete };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to refresh system."));
    }
  }
);

// Empty content tells the server to seed with SYSTEM_TEMPLATE_HTML — a
// documented backend behavior (see Docs/API/contract.md).
export const createContract = createAsyncThunk(
  "contractManagement/createContract",
  async ({ name }, { rejectWithValue }) => {
    try {
      const data = await request(`${API()}/contracts/templates/`, {
        method: "POST",
        body: JSON.stringify({
          name: name || "Untitled Template",
          content: "",
        }),
      });
      return normalizeUserTemplate(data);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to create."));
    }
  }
);

// System templates clone via a dedicated action; user rows use the detail
// clone action. Both accept an optional `name`.
export const duplicateContract = createAsyncThunk(
  "contractManagement/duplicateContract",
  async (contractId, { getState, rejectWithValue }) => {
    try {
      const isSystem = String(contractId) === SYSTEM_TEMPLATE_ID;
      const state = getState().contractManagement;
      const source = state.templates.find(
        (t) => String(t.id) === String(contractId)
      );
      const url = isSystem
        ? `${API()}/contracts/templates/system/clone/`
        : `${API()}/contracts/templates/${contractId}/clone/`;
      const name = source ? `Copy of ${source.name}` : undefined;
      const body = name ? JSON.stringify({ name }) : JSON.stringify({});
      const data = await request(url, { method: "POST", body });
      return normalizeUserTemplate(data);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to duplicate."));
    }
  }
);

export const renameContract = createAsyncThunk(
  "contractManagement/renameContract",
  async ({ contractId, name }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${API()}/contracts/templates/${contractId}/`,
        { method: "PATCH", body: JSON.stringify({ name }) }
      );
      return { contractId, name: data.name || name };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to rename."));
    }
  }
);

// PATCH content. Field name in the payload is `content` (backend); we keep
// `content_html` as the internal state alias.
export const saveContract = createAsyncThunk(
  "contractManagement/saveContract",
  async ({ contractId, content_html }, { rejectWithValue }) => {
    try {
      const data = await request(
        `${API()}/contracts/templates/${contractId}/`,
        { method: "PATCH", body: JSON.stringify({ content: content_html }) }
      );
      return {
        contractId,
        content_html: data.content ?? content_html,
        updated_at: data.updated_at || new Date().toISOString(),
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to save."));
    }
  }
);

// Promote a template to the caller's default via the dedicated set-default
// endpoint (POST, no body). The System Built-in has its own route. Backend
// clears the flag on the other rows and recomputes the system row's flag, so we
// refetch the whole list afterward to keep every is_default in sync.
export const setContractAsDefault = createAsyncThunk(
  "contractManagement/setContractAsDefault",
  async (contractId, { dispatch, rejectWithValue }) => {
    try {
      const isSystem = String(contractId) === SYSTEM_TEMPLATE_ID;
      const url = isSystem
        ? `${API()}/contracts/templates/system/set-default/`
        : `${API()}/contracts/templates/${contractId}/set-default/`;
      await request(url, { method: "POST", body: JSON.stringify({}) });
      await dispatch(fetchContracts());
      return { contractId };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to set default."));
    }
  }
);

// Soft delete. If we removed the current default, the system row's computed
// `is_default` flips — refetch it so the badge updates.
export const deleteContract = createAsyncThunk(
  "contractManagement/deleteContract",
  async (contractId, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState().contractManagement;
      const target = state.templates.find(
        (t) => String(t.id) === String(contractId)
      );
      const wasDefault = !!target?.is_default;
      await request(`${API()}/contracts/templates/${contractId}/`, {
        method: "DELETE",
      });
      // Awaited so `deletedFallbackName` populates before deleteContract's
      // fulfilled handler fires — the container's delete-success toast reads
      // both in the same effect and would miss the fallback name otherwise.
      if (wasDefault) {
        await dispatch(refreshSystemTemplate({ fromDelete: true }));
      }
      return { contractId, wasDefault };
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to delete."));
    }
  }
);

// Direct helper (not a thunk — no state footprint) used by the editor
// toolbar's Insert Image button. Returns the public URL on success.
export const uploadTemplateImage = async (file) => {
  const formData = new FormData();
  formData.append("image", file);
  const data = await request(`${API()}/contracts/templates/images/`, {
    method: "POST",
    body: formData,
    headers: {}, // sentinel — request.js strips Content-Type for FormData
  });
  return data;
};

// --- Slice ------------------------------------------------------------------

const initialState = {
  templates: [],
  templatesCount: 0,
  getTemplatesStatus: "idle",
  getTemplatesError: null,

  selectedTemplate: null,
  getTemplateStatus: "idle",
  getTemplateError: null,

  saveTemplateStatus: "idle",
  saveTemplateError: null,

  createTemplateStatus: "idle",
  createTemplateError: null,
  createdTemplateId: null,

  duplicateTemplateStatus: "idle",
  duplicateTemplateError: null,
  duplicatedTemplateId: null,

  renameTemplateStatus: "idle",
  renameTemplateError: null,

  deleteTemplateStatus: "idle",
  deleteTemplateError: null,
  deletedFallbackName: null,

  setDefaultStatus: "idle",
  setDefaultError: null,
};

// Replace the system row in-place. Used by the refreshSystemTemplate follow-up
// so we don't stomp the rest of the list.
const upsertSystemRow = (templates, systemRow) => {
  const idx = templates.findIndex((t) => t.is_system);
  if (idx === -1) return [systemRow, ...templates];
  const next = templates.slice();
  next[idx] = { ...next[idx], ...systemRow };
  return next;
};

const contractManagementSlice = createSlice({
  name: "contractManagement",
  initialState,
  reducers: {
    clearCreateStatus: (state) => {
      state.createTemplateStatus = "idle";
      state.createTemplateError = null;
      state.createdTemplateId = null;
    },
    clearDuplicateStatus: (state) => {
      state.duplicateTemplateStatus = "idle";
      state.duplicateTemplateError = null;
      state.duplicatedTemplateId = null;
    },
    clearSaveStatus: (state) => {
      state.saveTemplateStatus = "idle";
      state.saveTemplateError = null;
    },
    clearRenameStatus: (state) => {
      state.renameTemplateStatus = "idle";
      state.renameTemplateError = null;
    },
    clearDeleteStatus: (state) => {
      state.deleteTemplateStatus = "idle";
      state.deleteTemplateError = null;
      state.deletedFallbackName = null;
    },
    clearSetDefaultStatus: (state) => {
      state.setDefaultStatus = "idle";
      state.setDefaultError = null;
    },
    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
      state.getTemplateStatus = "idle";
      state.getTemplateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(fetchContracts.pending, (state) => {
        state.getTemplatesStatus = "loading";
        state.getTemplatesError = null;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.getTemplatesStatus = "success";
        state.templates = action.payload.templates;
        state.templatesCount = action.payload.count;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        state.getTemplatesStatus = "failed";
        state.getTemplatesError = action.payload || "Failed to load.";
      })

      // Detail
      .addCase(fetchContractById.pending, (state) => {
        state.getTemplateStatus = "loading";
        state.getTemplateError = null;
      })
      .addCase(fetchContractById.fulfilled, (state, action) => {
        state.getTemplateStatus = "success";
        state.selectedTemplate = action.payload;
      })
      .addCase(fetchContractById.rejected, (state, action) => {
        state.getTemplateStatus = "failed";
        state.getTemplateError = action.payload || "Failed to load.";
        state.selectedTemplate = null;
      })

      // Refresh system row (follow-up from default/delete). Update the list
      // AND the selected template (if it happens to be the system row).
      .addCase(refreshSystemTemplate.fulfilled, (state, action) => {
        const { row, fromDelete } = action.payload;
        state.templates = upsertSystemRow(state.templates, row);
        if (state.selectedTemplate?.is_system) {
          state.selectedTemplate = { ...state.selectedTemplate, ...row };
        }
        // Only stamp the fallback-to-system copy on the delete-of-default path.
        if (fromDelete && row.is_default) {
          state.deletedFallbackName = row.name;
        }
      })

      // Create
      .addCase(createContract.pending, (state) => {
        state.createTemplateStatus = "loading";
        state.createTemplateError = null;
      })
      .addCase(createContract.fulfilled, (state, action) => {
        state.createTemplateStatus = "success";
        state.templates = [...state.templates, action.payload];
        state.createdTemplateId = action.payload.id;
      })
      .addCase(createContract.rejected, (state, action) => {
        state.createTemplateStatus = "failed";
        state.createTemplateError = action.payload || "Failed to create.";
      })

      // Duplicate
      .addCase(duplicateContract.pending, (state) => {
        state.duplicateTemplateStatus = "loading";
        state.duplicateTemplateError = null;
      })
      .addCase(duplicateContract.fulfilled, (state, action) => {
        state.duplicateTemplateStatus = "success";
        state.templates = [...state.templates, action.payload];
        state.duplicatedTemplateId = action.payload.id;
      })
      .addCase(duplicateContract.rejected, (state, action) => {
        state.duplicateTemplateStatus = "failed";
        state.duplicateTemplateError = action.payload || "Failed to duplicate.";
      })

      // Rename
      .addCase(renameContract.pending, (state) => {
        state.renameTemplateStatus = "loading";
        state.renameTemplateError = null;
      })
      .addCase(renameContract.fulfilled, (state, action) => {
        state.renameTemplateStatus = "success";
        const { contractId, name } = action.payload;
        state.templates = state.templates.map((t) =>
          String(t.id) === String(contractId) ? { ...t, name } : t
        );
        if (
          state.selectedTemplate &&
          String(state.selectedTemplate.id) === String(contractId)
        ) {
          state.selectedTemplate = { ...state.selectedTemplate, name };
        }
      })
      .addCase(renameContract.rejected, (state, action) => {
        state.renameTemplateStatus = "failed";
        state.renameTemplateError = action.payload || "Failed to rename.";
      })

      // Save
      .addCase(saveContract.pending, (state) => {
        state.saveTemplateStatus = "loading";
        state.saveTemplateError = null;
      })
      .addCase(saveContract.fulfilled, (state, action) => {
        state.saveTemplateStatus = "success";
        const { contractId, content_html, updated_at } = action.payload;
        state.templates = state.templates.map((t) =>
          String(t.id) === String(contractId)
            ? { ...t, content_html, updated_at }
            : t
        );
        if (
          state.selectedTemplate &&
          String(state.selectedTemplate.id) === String(contractId)
        ) {
          state.selectedTemplate = {
            ...state.selectedTemplate,
            content_html,
            updated_at,
          };
        }
      })
      .addCase(saveContract.rejected, (state, action) => {
        state.saveTemplateStatus = "failed";
        state.saveTemplateError = action.payload || "Failed to save.";
      })

      // Set as default (only user rows can be targeted; system disables it)
      .addCase(setContractAsDefault.pending, (state) => {
        state.setDefaultStatus = "loading";
        state.setDefaultError = null;
      })
      .addCase(setContractAsDefault.fulfilled, (state, action) => {
        state.setDefaultStatus = "success";
        // state.templates was already refreshed by the fetchContracts follow-up
        // inside the thunk; just sync the open editor's row (works for the
        // system row too, since contractId is the "system" sentinel there).
        const { contractId } = action.payload;
        if (state.selectedTemplate) {
          state.selectedTemplate = {
            ...state.selectedTemplate,
            is_default:
              String(state.selectedTemplate.id) === String(contractId),
          };
        }
      })
      .addCase(setContractAsDefault.rejected, (state, action) => {
        state.setDefaultStatus = "failed";
        state.setDefaultError = action.payload || "Failed to set default.";
      })

      // Delete — backend handles the cascade; refreshSystemTemplate follow-up
      // updates the system row's computed is_default when we removed the
      // current default.
      .addCase(deleteContract.pending, (state) => {
        state.deleteTemplateStatus = "loading";
        state.deleteTemplateError = null;
      })
      .addCase(deleteContract.fulfilled, (state, action) => {
        state.deleteTemplateStatus = "success";
        const { contractId } = action.payload;
        state.templates = state.templates.filter(
          (t) => String(t.id) !== String(contractId)
        );
      })
      .addCase(deleteContract.rejected, (state, action) => {
        state.deleteTemplateStatus = "failed";
        state.deleteTemplateError = action.payload || "Failed to delete.";
      });
  },
});

export const {
  clearCreateStatus,
  clearDuplicateStatus,
  clearSaveStatus,
  clearRenameStatus,
  clearDeleteStatus,
  clearSetDefaultStatus,
  clearSelectedTemplate,
} = contractManagementSlice.actions;

export default contractManagementSlice.reducer;
